import { useState, useRef, useEffect, useCallback } from "react";

export interface ChatMsg {
  id?: number;
  inquiry_id: number;
  sender_type: "customer" | "admin";
  sender_name: string;
  message: string;
  attachment_url?: string;
  file_url?: string;
  file_name?: string;
  file_type?: string;
  created_at: string;
  is_recalled?: boolean;
  is_edited?: boolean;
  edited_at?: string;
}

// ── Helpers ──
export const isImgUrl = (url: string, fileType?: string) => {
  if (fileType && fileType.startsWith("image/")) return true;
  return /\.(jpg|jpeg|png|gif|webp|svg)(\?|$)/i.test(url);
};
export const isPdfUrl = (url: string, fileType?: string, fileName?: string) => {
  if (fileType === "application/pdf" || fileType === "pdf") return true;
  if (fileName && fileName.toLowerCase().endsWith(".pdf")) return true;
  return /\.pdf(\?|$)/i.test(url);
};
export const isAudioUrl = (url: string, fileType?: string) => {
  if (fileType && fileType.startsWith("audio/")) return true;
  return /\.(mp3|wav|ogg|m4a|aac|flac)(\?|$)/i.test(url);
};

// ── Image Lightbox Modal ──
export const ImageLightbox = ({ url, onClose }: { url: string; onClose: () => void }) => {
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);

  const handleSave = async (e: React.MouseEvent) => {
    e.stopPropagation();
    setSaving(true);
    try {
      const response = await fetch(url);
      const blob = await response.blob();
      const blobUrl = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = blobUrl;
      const ext = url.split(".").pop()?.split("?")[0] ?? "jpg";
      a.download = `image_${Date.now()}.${ext}`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(blobUrl);
      setSaved(true);
      setTimeout(() => setSaved(false), 2500);
    } catch {
      window.open(url, "_blank");
    }
    setSaving(false);
  };

  useEffect(() => {
    const handleKey = (e: KeyboardEvent) => { if (e.key === "Escape") onClose(); };
    document.addEventListener("keydown", handleKey);
    return () => document.removeEventListener("keydown", handleKey);
  }, [onClose]);

  return (
    <div className="fixed inset-0 z-[500] bg-black/90 flex flex-col items-center justify-center" onClick={onClose}>
      <div
        className="absolute top-0 left-0 right-0 flex items-center justify-between px-5 py-3 bg-gradient-to-b from-black/60 to-transparent"
        onClick={(e) => e.stopPropagation()}
      >
        <span className="text-white/70 text-sm">Xem ảnh</span>
        <div className="flex items-center gap-2">
          <button
            onClick={handleSave}
            disabled={saving}
            className="flex items-center gap-2 px-4 py-2 bg-white/20 hover:bg-white/30 text-white text-sm font-semibold rounded-xl cursor-pointer transition-colors whitespace-nowrap"
          >
            {saving ? <i className="ri-loader-4-line animate-spin" /> : saved ? <i className="ri-check-line text-green-400" /> : <i className="ri-download-line" />}
            {saved ? "Đã lưu!" : "Lưu ảnh"}
          </button>
          <button onClick={onClose} className="w-9 h-9 flex items-center justify-center bg-white/20 hover:bg-white/30 rounded-xl cursor-pointer transition-colors">
            <i className="ri-close-line text-white text-lg" />
          </button>
        </div>
      </div>
      <img src={url} alt="preview" className="max-w-[90vw] max-h-[85vh] object-contain rounded-xl" onClick={(e) => e.stopPropagation()} />
    </div>
  );
};

// ── PDF Viewer Modal — browser native viewer, no Google Docs ──
export const PdfViewerModal = ({ url, fileName, onClose }: { url: string; fileName?: string; onClose: () => void }) => {
  const displayName = fileName || decodeURIComponent(url.split("/").pop()?.split("?")[0] ?? "document.pdf");
  // Append #toolbar=1 for inline browser PDF toolbar
  const viewerSrc = `${url}#toolbar=1&navpanes=1&scrollbar=1`;

  return (
    <div className="fixed inset-0 z-[300] bg-black/85 flex flex-col" onClick={onClose}>
      {/* Header */}
      <div className="flex items-center justify-between px-5 py-3 bg-gray-900 flex-shrink-0" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center gap-3">
          <div className="w-8 h-8 flex items-center justify-center bg-red-500/20 rounded-lg">
            <i className="ri-file-pdf-2-line text-red-400 text-base" />
          </div>
          <div>
            <p className="text-white text-sm font-semibold truncate max-w-[350px]">{displayName}</p>
            <p className="text-white/40 text-[10px]">Tài liệu PDF · Xem trực tiếp trong trình duyệt</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <a
            href={url}
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center gap-1.5 px-3 py-1.5 bg-white/20 hover:bg-white/30 text-white text-xs font-semibold rounded-lg cursor-pointer transition-colors whitespace-nowrap"
            onClick={(e) => e.stopPropagation()}
          >
            <i className="ri-external-link-line" /> Mở tab mới
          </a>
          <a
            href={url}
            download={displayName}
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center gap-1.5 px-3 py-1.5 bg-red-500 hover:bg-red-600 text-white text-xs font-semibold rounded-lg cursor-pointer transition-colors whitespace-nowrap"
            onClick={(e) => e.stopPropagation()}
          >
            <i className="ri-download-line" /> Tải xuống
          </a>
          <button onClick={onClose} className="w-8 h-8 flex items-center justify-center bg-white/10 hover:bg-white/20 rounded-lg cursor-pointer transition-colors">
            <i className="ri-close-line text-white" />
          </button>
        </div>
      </div>

      {/* Viewer — native browser PDF renderer */}
      <div className="flex-1 overflow-hidden bg-gray-800" onClick={(e) => e.stopPropagation()}>
        <iframe
          src={viewerSrc}
          className="w-full h-full border-0"
          title={displayName}
        />
      </div>

      {/* Fallback bar */}
      <div className="flex items-center justify-center gap-3 px-5 py-2.5 bg-gray-900 border-t border-white/10 flex-shrink-0" onClick={(e) => e.stopPropagation()}>
        <i className="ri-information-line text-white/40 text-xs" />
        <p className="text-white/40 text-xs">Nếu iframe không hiển thị,</p>
        <a href={url} target="_blank" rel="noopener noreferrer" className="text-xs text-red-400 hover:text-red-300 font-semibold cursor-pointer underline whitespace-nowrap">
          nhấn đây để mở file trực tiếp trong tab mới
        </a>
      </div>
    </div>
  );
};

// ── PDF Card — modern SaaS style ──
export const PdfCard = ({
  url,
  fileName,
  isOwn,
  onPreview,
}: {
  url: string;
  fileName?: string;
  isOwn: boolean;
  onPreview: () => void;
}) => {
  const displayName = fileName || decodeURIComponent(url.split("/").pop()?.split("?")[0] ?? "document.pdf");

  return (
    <div
      className={`rounded-2xl border overflow-hidden transition-all hover:scale-[1.01] ${
        isOwn
          ? "bg-gradient-to-br from-[#003d80] to-[#002a5c] border-white/10"
          : "bg-white border-gray-200"
      }`}
      style={{ minWidth: 260, maxWidth: 320 }}
    >
      {/* Top section */}
      <div className="flex items-center gap-3 px-4 py-3">
        {/* PDF icon */}
        <div className="w-12 h-12 flex items-center justify-center bg-red-500/15 rounded-xl flex-shrink-0 border border-red-500/20">
          <i className="ri-file-pdf-2-line text-red-500 text-2xl" />
        </div>
        <div className="flex-1 min-w-0">
          <p className={`text-sm font-bold truncate ${isOwn ? "text-white" : "text-gray-900"}`} title={displayName}>
            {displayName}
          </p>
          <div className="flex items-center gap-1.5 mt-0.5">
            <span className="text-[10px] font-semibold px-1.5 py-0.5 rounded bg-red-500/15 text-red-500">PDF</span>
            <span className={`text-[10px] ${isOwn ? "text-white/50" : "text-gray-400"}`}>Tài liệu đính kèm</span>
          </div>
        </div>
      </div>

      {/* Divider */}
      <div className={`h-px mx-4 ${isOwn ? "bg-white/10" : "bg-gray-100"}`} />

      {/* Action buttons */}
      <div className="flex">
        {/* Preview button */}
        <button
          onClick={onPreview}
          className={`flex-1 flex items-center justify-center gap-2 py-2.5 text-xs font-semibold cursor-pointer transition-colors ${
            isOwn
              ? "text-white/80 hover:bg-white/10"
              : "text-gray-600 hover:bg-gray-50"
          }`}
        >
          <i className="ri-eye-line text-sm" />
          Xem file
        </button>

        <div className={`w-px ${isOwn ? "bg-white/10" : "bg-gray-100"}`} />

        {/* Open in new tab */}
        <a
          href={url}
          target="_blank"
          rel="noopener noreferrer"
          className={`flex-1 flex items-center justify-center gap-2 py-2.5 text-xs font-semibold cursor-pointer transition-colors ${
            isOwn
              ? "text-white/80 hover:bg-white/10"
              : "text-gray-600 hover:bg-gray-50"
          }`}
          onClick={(e) => e.stopPropagation()}
        >
          <i className="ri-external-link-line text-sm" />
          Mở tab mới
        </a>

        <div className={`w-px ${isOwn ? "bg-white/10" : "bg-gray-100"}`} />

        {/* Download */}
        <a
          href={url}
          download={displayName}
          target="_blank"
          rel="noopener noreferrer"
          className="flex-1 flex items-center justify-center gap-2 py-2.5 text-xs font-semibold cursor-pointer transition-colors bg-red-500 hover:bg-red-600 text-white"
          onClick={(e) => e.stopPropagation()}
        >
          <i className="ri-download-line text-sm" />
          Tải xuống
        </a>
      </div>
    </div>
  );
};

// ── Image Bubble with lightbox ──
export const ImageBubble = ({ url, isOwn }: { url: string; isOwn: boolean }) => {
  const [lightboxOpen, setLightboxOpen] = useState(false);

  return (
    <>
      {lightboxOpen && <ImageLightbox url={url} onClose={() => setLightboxOpen(false)} />}
      <div
        className="relative group/img rounded-xl overflow-hidden border border-gray-200 cursor-pointer"
        onClick={() => setLightboxOpen(true)}
      >
        <img src={url} alt="attachment" className="max-w-[240px] max-h-[200px] object-cover rounded-xl block" />
        <div className="absolute inset-0 bg-black/0 group-hover/img:bg-black/25 transition-all rounded-xl flex items-center justify-center">
          <div className="opacity-0 group-hover/img:opacity-100 transition-opacity w-10 h-10 flex items-center justify-center bg-white/80 rounded-full">
            <i className="ri-zoom-in-line text-gray-800 text-lg" />
          </div>
        </div>
      </div>
    </>
  );
};

// ── Generic File Card ──
const GenericFileCard = ({ url, fileName, isOwn }: { url: string; fileName?: string; isOwn: boolean }) => {
  const displayName = fileName || decodeURIComponent(url.split("/").pop()?.split("?")[0] ?? "file");
  const ext = displayName.split(".").pop()?.toUpperCase() ?? "FILE";

  const extColors: Record<string, string> = {
    DOC: "text-blue-500 bg-blue-500/10",
    DOCX: "text-blue-500 bg-blue-500/10",
    XLS: "text-green-500 bg-green-500/10",
    XLSX: "text-green-500 bg-green-500/10",
    PPT: "text-orange-500 bg-orange-500/10",
    PPTX: "text-orange-500 bg-orange-500/10",
    ZIP: "text-yellow-600 bg-yellow-500/10",
    RAR: "text-yellow-600 bg-yellow-500/10",
  };
  const extColor = extColors[ext] ?? "text-gray-500 bg-gray-100";

  return (
    <div
      className={`rounded-2xl border overflow-hidden ${isOwn ? "bg-[#003d80] border-white/10" : "bg-white border-gray-200"}`}
      style={{ minWidth: 240, maxWidth: 300 }}
    >
      <div className="flex items-center gap-3 px-4 py-3">
        <div className={`w-12 h-12 flex items-center justify-center rounded-xl flex-shrink-0 border ${extColor} border-current/20`}>
          <i className="ri-file-line text-2xl" />
        </div>
        <div className="flex-1 min-w-0">
          <p className={`text-sm font-bold truncate ${isOwn ? "text-white" : "text-gray-900"}`} title={displayName}>
            {displayName}
          </p>
          <div className="flex items-center gap-1.5 mt-0.5">
            <span className={`text-[10px] font-semibold px-1.5 py-0.5 rounded ${extColor}`}>{ext}</span>
            <span className={`text-[10px] ${isOwn ? "text-white/50" : "text-gray-400"}`}>Tệp đính kèm</span>
          </div>
        </div>
      </div>
      <div className={`h-px mx-4 ${isOwn ? "bg-white/10" : "bg-gray-100"}`} />
      <a
        href={url}
        target="_blank"
        rel="noopener noreferrer"
        download={displayName}
        className="flex items-center justify-center gap-2 w-full py-2.5 bg-gray-600 hover:bg-gray-700 text-white text-xs font-semibold cursor-pointer transition-colors"
      >
        <i className="ri-download-line" /> Tải xuống
      </a>
    </div>
  );
};

// ── Mini Audio Player ──
const AudioPlayer = ({ url, fileName, isOwn }: { url: string; fileName?: string; isOwn: boolean }) => {
  const audioRef = useRef<HTMLAudioElement>(null);
  const [playing, setPlaying] = useState(false);
  const [progress, setProgress] = useState(0);
  const [duration, setDuration] = useState(0);
  const [currentTime, setCurrentTime] = useState(0);
  const displayName = fileName || decodeURIComponent(url.split("/").pop()?.split("?")[0] ?? "audio");

  const togglePlay = () => {
    if (!audioRef.current) return;
    if (playing) { audioRef.current.pause(); } else { audioRef.current.play().catch(() => {}); }
    setPlaying(!playing);
  };
  const handleTimeUpdate = () => {
    if (!audioRef.current) return;
    setCurrentTime(audioRef.current.currentTime);
    setProgress((audioRef.current.currentTime / (audioRef.current.duration || 1)) * 100);
  };
  const handleLoadedMetadata = () => { if (audioRef.current) setDuration(audioRef.current.duration); };
  const handleEnded = () => setPlaying(false);
  const handleSeek = (e: React.MouseEvent<HTMLDivElement>) => {
    if (!audioRef.current || !duration) return;
    const rect = e.currentTarget.getBoundingClientRect();
    audioRef.current.currentTime = ((e.clientX - rect.left) / rect.width) * duration;
  };
  const fmt = (s: number) => `${Math.floor(s / 60)}:${Math.floor(s % 60).toString().padStart(2, "0")}`;

  return (
    <div className={`rounded-2xl border overflow-hidden ${isOwn ? "bg-[#003d80] border-white/10" : "bg-white border-gray-200"}`} style={{ minWidth: 240 }}>
      <div className="flex items-center gap-3 px-4 py-3">
        <div className={`w-10 h-10 flex items-center justify-center rounded-xl flex-shrink-0 ${isOwn ? "bg-white/15" : "bg-[#004A99]/10"}`}>
          <i className={`ri-music-line text-lg ${isOwn ? "text-white" : "text-[#004A99]"}`} />
        </div>
        <div className="flex-1 min-w-0">
          <p className={`text-xs font-bold truncate ${isOwn ? "text-white" : "text-gray-800"}`}>{displayName}</p>
          <p className={`text-[10px] ${isOwn ? "text-white/50" : "text-gray-400"}`}>Tệp âm thanh</p>
        </div>
      </div>
      <div className="px-4 pb-3">
        <audio ref={audioRef} src={url} onTimeUpdate={handleTimeUpdate} onLoadedMetadata={handleLoadedMetadata} onEnded={handleEnded} preload="metadata" />
        <div className="flex items-center gap-3">
          <button
            onClick={togglePlay}
            className={`w-9 h-9 flex items-center justify-center rounded-full flex-shrink-0 cursor-pointer transition-colors ${isOwn ? "bg-white/20 hover:bg-white/30 text-white" : "bg-[#004A99] hover:bg-[#003a7a] text-white"}`}
          >
            <i className={`text-sm ${playing ? "ri-pause-fill" : "ri-play-fill"}`} />
          </button>
          <div className="flex-1">
            <div className={`h-1.5 rounded-full cursor-pointer relative ${isOwn ? "bg-white/20" : "bg-gray-200"}`} onClick={handleSeek}>
              <div className={`h-full rounded-full transition-all ${isOwn ? "bg-white" : "bg-[#004A99]"}`} style={{ width: `${progress}%` }} />
            </div>
            <div className={`flex justify-between mt-1 text-[10px] ${isOwn ? "text-white/50" : "text-gray-400"}`}>
              <span>{fmt(currentTime)}</span>
              <span>{fmt(duration)}</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

// ── Context Menu ──
interface ContextMenuProps {
  x: number;
  y: number;
  msg: ChatMsg;
  isOwn: boolean;
  onClose: () => void;
  onCopy: () => void;
  onRecall: () => void;
  onEdit: () => void;
  onShare: () => void;
}

export const MessageContextMenu = ({ x, y, msg, isOwn, onClose, onCopy, onRecall, onEdit, onShare }: ContextMenuProps) => {
  const menuRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClick = () => onClose();
    document.addEventListener("mousedown", handleClick);
    return () => document.removeEventListener("mousedown", handleClick);
  }, [onClose]);

  const adjustedX = Math.min(x, window.innerWidth - 200);
  const adjustedY = Math.min(y, window.innerHeight - 260);
  const downloadUrl = msg.file_url || msg.attachment_url;
  const downloadName = msg.file_name;

  return (
    <div
      ref={menuRef}
      className="fixed z-[400] bg-white rounded-xl border border-gray-200 py-1 min-w-[180px]"
      style={{ left: adjustedX, top: adjustedY, boxShadow: "0 8px 24px rgba(0,0,0,0.14)" }}
      onMouseDown={(e) => e.stopPropagation()}
    >
      {msg.message && !msg.is_recalled && (
        <button onClick={() => { onCopy(); onClose(); }} className="w-full flex items-center gap-2.5 px-3 py-2 text-sm text-gray-700 hover:bg-gray-50 cursor-pointer transition-colors">
          <i className="ri-file-copy-line text-gray-400 text-base" /> Sao chép
        </button>
      )}
      {downloadUrl && !msg.is_recalled && (
        <a
          href={downloadUrl}
          download={downloadName}
          target="_blank"
          rel="noopener noreferrer"
          className="w-full flex items-center gap-2.5 px-3 py-2 text-sm text-gray-700 hover:bg-gray-50 cursor-pointer transition-colors"
          onClick={onClose}
        >
          <i className="ri-download-line text-gray-400 text-base" /> Tải về / Lưu file
        </a>
      )}
      <button onClick={() => { onShare(); onClose(); }} className="w-full flex items-center gap-2.5 px-3 py-2 text-sm text-gray-700 hover:bg-gray-50 cursor-pointer transition-colors">
        <i className="ri-share-line text-gray-400 text-base" /> Chia sẻ
      </button>
      {isOwn && !msg.is_recalled && (
        <>
          <div className="h-px bg-gray-100 my-1" />
          {msg.message && (
            <button onClick={() => { onEdit(); onClose(); }} className="w-full flex items-center gap-2.5 px-3 py-2 text-sm text-amber-600 hover:bg-amber-50 cursor-pointer transition-colors">
              <i className="ri-edit-line text-amber-400 text-base" /> Chỉnh sửa
            </button>
          )}
          <button onClick={() => { onRecall(); onClose(); }} className="w-full flex items-center gap-2.5 px-3 py-2 text-sm text-red-600 hover:bg-red-50 cursor-pointer transition-colors">
            <i className="ri-arrow-go-back-line text-red-400 text-base" /> Thu hồi tin nhắn
          </button>
        </>
      )}
    </div>
  );
};

// Highlight search text
const HighlightText = ({ text, query }: { text: string; query?: string }) => {
  if (!query || !query.trim()) return <>{text}</>;
  const parts = text.split(new RegExp(`(${query.replace(/[.*+?^${}()|[\]\\]/g, "\\$&")})`, "gi"));
  return (
    <>
      {parts.map((part, i) =>
        part.toLowerCase() === query.toLowerCase() ? (
          <mark key={i} className="bg-yellow-300 text-gray-900 rounded px-0.5">{part}</mark>
        ) : part
      )}
    </>
  );
};

// ── Main ChatBubble ──
interface ChatBubbleProps {
  msg: ChatMsg;
  isOwn: boolean;
  isCopied: boolean;
  onContextMenu: (e: React.MouseEvent, msg: ChatMsg) => void;
  onCopy: (msg: ChatMsg) => void;
  onOpenContextMenu: (e: React.MouseEvent, msg: ChatMsg) => void;
  onPdfPreview: (url: string, fileName?: string) => void;
  ownerLabel?: string;
  otherLabel?: string;
  searchQuery?: string;
}

const ChatBubble = ({
  msg,
  isOwn,
  isCopied,
  onContextMenu,
  onCopy,
  onOpenContextMenu,
  onPdfPreview,
  ownerLabel = "Bạn",
  otherLabel = "Đối tác",
  searchQuery = "",
}: ChatBubbleProps) => {
  // Effective attachment URL — prefer file_url
  const effectiveAttachUrl = msg.file_url || msg.attachment_url;
  const effectiveFileName = msg.file_name;
  const effectiveFileType = msg.file_type;

  // Detect type using all available signals
  const hasImg = effectiveAttachUrl && isImgUrl(effectiveAttachUrl, effectiveFileType);
  const hasPdf = effectiveAttachUrl && isPdfUrl(effectiveAttachUrl, effectiveFileType, effectiveFileName);
  const hasAudio = effectiveAttachUrl && isAudioUrl(effectiveAttachUrl, effectiveFileType);
  const hasOtherFile = effectiveAttachUrl && !hasImg && !hasPdf && !hasAudio;

  const isHighlighted = searchQuery && msg.message?.toLowerCase().includes(searchQuery.toLowerCase());

  // Hide placeholder text when we have a real file to show
  const PLACEHOLDER_MSGS = [
    "Đã gửi một tài liệu PDF",
    "Đã gửi một tệp đính kèm",
    "Đã gửi một ảnh",
    "Đã gửi một file",
  ];
  const isPlaceholderMsg = effectiveAttachUrl && PLACEHOLDER_MSGS.includes(msg.message?.trim() ?? "");
  const showTextBubble = msg.message && !isPlaceholderMsg;

  return (
    <div
      className={`flex gap-3 group ${isOwn ? "flex-row-reverse" : ""} ${isHighlighted ? "ring-2 ring-yellow-300 rounded-2xl p-1 -m-1" : ""}`}
      onContextMenu={(e) => onContextMenu(e, msg)}
    >
      {/* Avatar */}
      <div className={`w-8 h-8 rounded-xl flex items-center justify-center flex-shrink-0 ${isOwn ? "bg-[#004A99]" : "bg-[#EDF2FB]"}`}>
        <i className={`text-sm ${isOwn ? "ri-user-3-line text-white" : "ri-shield-user-line text-[#004A99]"}`} />
      </div>

      {/* Content */}
      <div className={`max-w-[75%] flex flex-col ${isOwn ? "items-end" : "items-start"}`}>
        {/* Meta */}
        <div className={`flex items-center gap-2 mb-1 ${isOwn ? "flex-row-reverse" : ""}`}>
          <span className="text-[11px] font-semibold text-gray-600">{isOwn ? ownerLabel : otherLabel}</span>
          <span className="text-[10px] text-gray-300">{msg.created_at?.slice(0, 16)}</span>
          {msg.is_edited && !msg.is_recalled && (
            <span className="text-[10px] text-amber-500 font-medium italic">Đã chỉnh sửa</span>
          )}
          {isCopied && <span className="text-[10px] text-green-500 font-semibold">Đã sao chép!</span>}
        </div>

        {/* Recalled */}
        {msg.is_recalled ? (
          <div className={`px-4 py-2.5 rounded-2xl text-sm italic flex items-center gap-2 ${isOwn ? "bg-[#004A99]/20 text-[#004A99]/60 rounded-tr-sm" : "bg-gray-100 text-gray-400 rounded-tl-sm"}`}>
            <i className="ri-arrow-go-back-line text-xs" />
            Tin nhắn đã thu hồi
          </div>
        ) : (
          <>
            {/* Text bubble */}
            {showTextBubble && (
              <div className={`px-4 py-3 rounded-2xl text-sm leading-relaxed whitespace-pre-wrap select-text ${isOwn ? "bg-[#004A99] text-white rounded-tr-sm" : "bg-gray-100 text-gray-800 rounded-tl-sm"}`}>
                <HighlightText text={msg.message} query={searchQuery} />
              </div>
            )}

            {/* File attachment */}
            {effectiveAttachUrl && (
              <div className={`mt-1.5 flex flex-col ${isOwn ? "items-end" : "items-start"}`}>
                {hasImg && <ImageBubble url={effectiveAttachUrl} isOwn={isOwn} />}
                {hasPdf && (
                  <PdfCard
                    url={effectiveAttachUrl}
                    fileName={effectiveFileName}
                    isOwn={isOwn}
                    onPreview={() => onPdfPreview(effectiveAttachUrl, effectiveFileName)}
                  />
                )}
                {hasAudio && <AudioPlayer url={effectiveAttachUrl} fileName={effectiveFileName} isOwn={isOwn} />}
                {hasOtherFile && <GenericFileCard url={effectiveAttachUrl} fileName={effectiveFileName} isOwn={isOwn} />}
              </div>
            )}
          </>
        )}

        {/* Quick action buttons on hover */}
        {!msg.is_recalled && (
          <div className={`flex items-center gap-1 mt-1 opacity-0 group-hover:opacity-100 transition-opacity ${isOwn ? "flex-row-reverse" : ""}`}>
            {msg.message && (
              <button onClick={() => onCopy(msg)} className="w-6 h-6 flex items-center justify-center rounded-md hover:bg-gray-100 cursor-pointer transition-colors" title="Sao chép">
                <i className="ri-file-copy-line text-gray-400 text-xs" />
              </button>
            )}
            {effectiveAttachUrl && (
              <a href={effectiveAttachUrl} download={effectiveFileName} target="_blank" rel="noopener noreferrer" className="w-6 h-6 flex items-center justify-center rounded-md hover:bg-gray-100 cursor-pointer transition-colors" title="Tải về">
                <i className="ri-download-line text-gray-400 text-xs" />
              </a>
            )}
            <button onClick={(e) => onOpenContextMenu(e, msg)} className="w-6 h-6 flex items-center justify-center rounded-md hover:bg-gray-100 cursor-pointer transition-colors" title="Thêm tùy chọn">
              <i className="ri-more-line text-gray-400 text-xs" />
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default ChatBubble;

// ── useScrollToBottom hook ──
export const useScrollToBottom = (containerRef: React.RefObject<HTMLDivElement | null>, deps: unknown[]) => {
  const scrollToBottom = useCallback(() => {
    const el = containerRef.current;
    if (!el) return;
    el.scrollTop = el.scrollHeight;
  }, [containerRef]);

  useEffect(() => {
    scrollToBottom();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, deps);

  return scrollToBottom;
};
